import { ChangeDetectionStrategy, Component, computed, inject, input, model } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { DspCreative, DspCreativeAdCreativeFormatProperties, MediaType } from "@front/m19-api-client";
import { DspCreativeAssetsService } from "@front/m19-services";
import {
  ICheckboxComponent,
  IIconComponent,
  ITableTdDirective,
  ModalService,
  ProductPreviewComponent,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  MediaPreviewModalComponent,
  MediaPreviewModalData,
} from "@m19-board/shared/media/media-preview-modal/media-preview-modal.component";
import { forkJoin, of, switchMap, take } from "rxjs";

// TODO(Hugo|Joseph): Remove this once the DspCreative type is updated and accepts array on asin and assetId
interface TemporaryDspCreative {
  adCreativeId: string;
  name: string;
  assetIds?: string[];
  asins?: string[];
  sizes?: Array<string>;
  adCreativeFormatProperties?: DspCreativeAdCreativeFormatProperties;
}

@Component({
  selector: "tr[app-dsp-custom-creatives-lines]",
  imports: [ITableTdDirective, ProductPreviewComponent, ICheckboxComponent, TranslocoDirective, IIconComponent],
  template: `
    <ng-container *transloco="let t">
      <td iTableTd class="w-0 p-1! pr-4!">
        <ICheckbox [(value)]="isSelected" />
      </td>
      <td iTableTd class="max-w-0">
        <span>{{ creative().name }}</span>
      </td>
      <td iTableTd>
        <div class="flex flex-wrap gap-2">
          @for (asset of creativeAssets(); track asset?.assetId) {
            <div class="group/creative relative flex size-18 items-center justify-center p-2">
              <div
                class="absolute hidden size-full items-center justify-center rounded-lg border bg-white/60 shadow-xs group-hover/creative:flex"
                (click)="previewCreative($event)"
              >
                <IIcon name="expand" class="text-xl text-gray-800" />
              </div>
              <img class="size-full object-contain" [src]="asset?.url" />
              <div
                class="type-caption absolute bottom-0 left-0 w-full bg-white/90 px-1 opacity-0 group-hover/creative:opacity-100"
              >
                {{ asset?.width }}x{{ asset?.height }}
              </div>
            </div>
          }
        </div>
      </td>

      <td iTableTd class="max-w-42">
        <div class="flex flex-wrap gap-2">
          @for (asin of temporaryCreative().asins; track asin) {
            <IProductPreview [productOrAsin]="asin" />
          }
        </div>
      </td>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspCustomCreativesLinesComponent {
  private readonly dspCreativeAssetService = inject(DspCreativeAssetsService);
  private readonly modalService = inject(ModalService);

  creative = input.required<DspCreative>();
  isSelected = model<boolean>(false);

  private creativeAsset = toSignal(
    toObservable(this.creative).pipe(
      switchMap((creative: DspCreative) => this.dspCreativeAssetService.getAssetById(creative.assetId!)),
    ),
  );

  temporaryCreative = computed(() => this.toTemporaryCreative(this.creative()));

  creativeAssets = toSignal(
    toObservable(this.temporaryCreative).pipe(
      switchMap((creative) => {
        if (!creative?.assetIds?.length) {
          return of([]);
        }
        return forkJoin(
          creative.assetIds.map((assetId) => this.dspCreativeAssetService.getAssetById(assetId).pipe(take(1))),
        );
      }),
    ),
  );

  previewCreative(event: PointerEvent) {
    event.stopPropagation();

    this.modalService.openModal<MediaPreviewModalData, void>(MediaPreviewModalComponent, {
      modalTitle: this.creative().name,
      data: {
        mediaSource: this.creativeAsset()!.url,
        mediaType: MediaType.customImage,
      },
    });
  }

  private toTemporaryCreative(creative: DspCreative): TemporaryDspCreative {
    return {
      adCreativeId: creative.adCreativeId,
      name: creative.name,
      assetIds: creative.assetId ? [creative.assetId] : undefined,
      asins: creative.asin ? [creative.asin] : undefined,
      sizes: creative.sizes,
    };
  }
}

export type MediaImageSize = { width: number; height: number; label: string };
export const RECOMMENDED_IMAGE_SIZES: MediaImageSize[] = [
  { width: 320, height: 50, label: "320x50" },
  { width: 300, height: 250, label: "300x250" },
  { width: 414, height: 125, label: "414x125" },
  { width: 728, height: 90, label: "728x90" },
  { width: 160, height: 600, label: "160x600" },
];
